import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/codeLocks';
import {BlockingGridFormattedRow} from 'types';
import {ProductBlockingCodesResponseData} from 'types/products-blocking';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: ProductBlockingCodesResponseData[] | undefined,
        t: TFunction,
    ) => BlockingGridFormattedRow[];
    headerControls?: ReactNode;
    headerControlsAlignItems?: string;
}

export const BlocksGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    headerControlsAlignItems,
}: Props) => {
    useGate(Gate);
    const {t} = useTranslation();
    const {data, dictionary, pageNumber, isLoading, filters, sortings} = useStore(tableState$);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [configFunc, dictionary, filters, sortings, t],
    );

    const mappedData = useMemo(() => dataMapper(data?.content, t), [data, dataMapper, t]);

    return (
        <Table
            columns={columns}
            data={mappedData}
            hasNext={data?.hasNext}
            pageIndex={pageNumber}
            onPageChange={pageNumberChanged}
            isLoading={isLoading}
            userConfig={{
                gridName: 'blockingGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            headerControls={headerControls}
            headerControlsAlignItems={headerControlsAlignItems}
            cellContainerSettings="width: 100%; min-height: 60px; word-break: break-word;"
        />
    );
};
